import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Box, IconButton, Menu, MenuItem, MenuList, Popover, Typography } from "@mui/material";
import { Rename } from "components/Prompts";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { RoleName } from "constants/index";
import { useModals, usePrompt, useUrlHooks } from "hooks";
import { toast } from "react-toastify";
import { useReportsQueriesSelector, useUserActions, useUserSelector } from "store";
import { IBookmark } from "store/types";

import { buildBookmarkSaveParams, getCurrentPageContext } from "./bookmark-page";
import { PeoplePicker } from "./PeoplePicker";

interface Props {
  bookmark: IBookmark;
  pageLabel: string;
  canOverrideFromCurrentPage: boolean;
}

export const ViewRowMenu: React.FC<Props> = ({ bookmark, pageLabel, canOverrideFromCurrentPage }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const shareItemRef = useRef<HTMLLIElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [shareAnchor, setShareAnchor] = useState<HTMLElement | null>(null);
  const closeShareTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openPrompt = usePrompt();
  const { updateBookmarkAction, deleteBookmarkAction, getBookmarksAction } = useUserActions();
  const { errorModal } = useModals();
  const { pathname, search } = useLocation();
  const { queryParams } = useUrlHooks();
  const reportsQueries = useReportsQueriesSelector();
  const user = useUserSelector();

  const canSetAccountDefault = user.email.endsWith("@altius.capital") && user.role.name === RoleName.Admin;

  const open = Boolean(anchorEl);

  const cancelCloseShare = useCallback(() => {
    if (closeShareTimer.current) {
      clearTimeout(closeShareTimer.current);
      closeShareTimer.current = null;
    }
  }, []);

  const closeShare = useCallback(() => {
    cancelCloseShare();
    setShareAnchor(null);
  }, [cancelCloseShare]);

  const scheduleCloseShare = useCallback(() => {
    cancelCloseShare();
    closeShareTimer.current = setTimeout(() => {
      closeShareTimer.current = null;
      setShareAnchor(null);
    }, 150);
  }, [cancelCloseShare]);

  const openShare = useCallback(() => {
    cancelCloseShare();
    if (shareItemRef.current) setShareAnchor(shareItemRef.current);
  }, [cancelCloseShare]);

  const closeMenu = useCallback(() => {
    closeShare();
    setAnchorEl(null);
  }, [closeShare]);

  useEffect(() => () => cancelCloseShare(), [cancelCloseShare]);

  const handleSetDefault = useCallback(async () => {
    closeMenu();
    try {
      await updateBookmarkAction({ id: bookmark.id, is_default: !bookmark.is_default });
      toast.success(bookmark.is_default ? "Default removed" : "Set as default");
    } catch {
      errorModal({ msg: "Something went wrong" });
    }
  }, [bookmark.id, bookmark.is_default, updateBookmarkAction, errorModal, closeMenu]);

  const handleRename = useCallback(async () => {
    closeMenu();
    try {
      const result = await openPrompt({
        Component: Rename,
        componentType: "popover",
        anchorEl: buttonRef.current as HTMLElement,
        props: { title: bookmark.name }
      });
      if (result) {
        await updateBookmarkAction({ id: bookmark.id, name: result });
        toast.success("View renamed");
      }
    } catch {
      errorModal({ msg: "Something went wrong" });
    }
  }, [bookmark.id, bookmark.name, openPrompt, updateBookmarkAction, errorModal, closeMenu]);

  const handleOverrideBookmark = useCallback(async () => {
    closeMenu();
    const page = getCurrentPageContext(pathname);
    if (!page) return;
    const built = buildBookmarkSaveParams(page, { pathname, search, queryParams, reportsQueries });
    if (!built) return;
    try {
      await updateBookmarkAction({
        id: bookmark.id,
        params: built.params,
        report_type: built.reportType
      });
      toast.success("View updated");
    } catch {
      errorModal({ msg: "Something went wrong" });
    }
  }, [bookmark.id, pathname, search, queryParams, reportsQueries, updateBookmarkAction, errorModal, closeMenu]);

  const handleShareScope = useCallback(
    async (next: "private" | "account") => {
      closeMenu();
      if (next === bookmark.visibility) return;
      try {
        await updateBookmarkAction({ id: bookmark.id, visibility: next });
        toast.success("Sharing updated");
      } catch {
        errorModal({ msg: "Something went wrong" });
      }
    },
    [bookmark.id, bookmark.visibility, updateBookmarkAction, errorModal, closeMenu]
  );

  const handleDelete = useCallback(async () => {
    closeMenu();
    try {
      await deleteBookmarkAction(bookmark.id);
      toast.success("View deleted");
    } catch {
      errorModal({ msg: "Something went wrong" });
    }
  }, [bookmark.id, deleteBookmarkAction, errorModal, closeMenu]);

  const handleToggleAccountDefault = useCallback(async () => {
    closeMenu();
    const turningOn = !bookmark.account_default;
    try {
      await updateBookmarkAction({
        id: bookmark.id,
        account_default: turningOn,
        ...(turningOn && bookmark.visibility !== "account" ? { visibility: "account" as const } : {})
      });
      await getBookmarksAction();
      toast.success(turningOn ? "Set as account default" : "Account default removed");
    } catch {
      errorModal({ msg: "Something went wrong" });
    }
  }, [
    bookmark.id,
    bookmark.account_default,
    bookmark.visibility,
    updateBookmarkAction,
    getBookmarksAction,
    errorModal,
    closeMenu
  ]);

  const handleOpenPeoplePicker = useCallback(async () => {
    const anchor = buttonRef.current;
    closeMenu();
    if (!anchor) return;
    const currentSharedWith = (bookmark.shared_with ?? []).map((u) => u.id);
    const result = await openPrompt({
      Component: PeoplePicker,
      componentType: "popover",
      anchorEl: anchor,
      anchorOrigin: { vertical: "bottom", horizontal: "right" },
      transformOrigin: { vertical: "top", horizontal: "right" },
      props: { initialSelectedIds: currentSharedWith }
    });
    if (!result) return;
    try {
      if (result.length === 0) {
        await updateBookmarkAction({ id: bookmark.id, visibility: "private" });
      } else {
        await updateBookmarkAction({ id: bookmark.id, visibility: "shared", shared_with: result });
      }
      toast.success("Sharing updated");
    } catch {
      errorModal({ msg: "Something went wrong" });
    }
  }, [openPrompt, closeMenu, bookmark, updateBookmarkAction, errorModal]);

  return (
    <>
      <IconButton
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        sx={{
          width: "24px",
          height: "24px",
          color: "#5e7a95"
        }}
      >
        <MoreVertIcon sx={{ fontSize: "16px" }} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        slotProps={{
          paper: {
            sx: {
              minWidth: "220px",
              borderRadius: "8px",
              border: "1px solid #dbe8f5",
              boxShadow:
                "0px 3px 3px -2px rgba(38,54,70,.16), 0px 3px 4px 0px rgba(38,54,70,.10), 0px 1px 8px 0px rgba(38,54,70,.08)",
              padding: "4px 0",
              overflow: "visible"
            }
          }
        }}
        MenuListProps={{ disablePadding: true, autoFocusItem: false }}
      >
        <MaterialTooltip
          isShow
          title={
            bookmark.is_default
              ? "This view loads automatically when you open this page. Click to stop."
              : `Loads this view automatically when you open ${pageLabel}.`
          }
          placement="left"
          variant="dark"
        >
          <MenuItem onClick={handleSetDefault} onMouseEnter={scheduleCloseShare} sx={menuItemSx}>
            <FlagOutlinedIcon sx={iconSx} />
            <Typography sx={labelSx}>
              {bookmark.is_default ? "Unset default for this page" : "Set as default for this page"}
            </Typography>
          </MenuItem>
        </MaterialTooltip>

        {canOverrideFromCurrentPage && (
          <MenuItem onClick={handleOverrideBookmark} onMouseEnter={scheduleCloseShare} sx={menuItemSx}>
            <SaveOutlinedIcon sx={iconSx} />
            <Typography sx={labelSx}>Save current view</Typography>
          </MenuItem>
        )}

        <MenuItem
          ref={shareItemRef}
          onMouseEnter={openShare}
          onMouseLeave={scheduleCloseShare}
          sx={{ ...menuItemSx, ...(Boolean(shareAnchor) && { backgroundColor: "#ecf1f8" }) }}
        >
          <IosShareOutlinedIcon sx={iconSx} />
          <Typography sx={labelSx}>Share</Typography>
          <ShareSummary visibility={bookmark.visibility} />
          <ChevronRightIcon sx={{ fontSize: "14px", color: "#92a7bc", ml: "4px" }} />
        </MenuItem>

        <MenuItem onClick={handleRename} onMouseEnter={scheduleCloseShare} sx={menuItemSx}>
          <EditOutlinedIcon sx={iconSx} />
          <Typography sx={labelSx}>Rename</Typography>
        </MenuItem>

        {canSetAccountDefault && [
          <Box key="account-default-divider" sx={{ height: "1px", backgroundColor: "#dbe8f5", margin: "4px 0" }} />,
          <MenuItem
            key="account-default-item"
            onClick={handleToggleAccountDefault}
            onMouseEnter={scheduleCloseShare}
            sx={menuItemSx}
          >
            <BusinessOutlinedIcon sx={iconSx} />
            <Typography sx={labelSx}>
              {bookmark.account_default ? "Unset account default" : "Set as account default"}
            </Typography>
          </MenuItem>
        ]}

        <Box sx={{ height: "1px", backgroundColor: "#dbe8f5", margin: "4px 0" }} />

        <MenuItem onClick={handleDelete} onMouseEnter={scheduleCloseShare} sx={menuItemSx}>
          <DeleteOutlineIcon sx={iconSx} />
          <Typography sx={labelSx}>Delete view</Typography>
        </MenuItem>
      </Menu>

      <Popover
        open={Boolean(shareAnchor) && open}
        anchorEl={shareAnchor}
        onClose={closeShare}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableRestoreFocus
        sx={{ pointerEvents: "none" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: "220px",
              ml: "4px",
              borderRadius: "8px",
              border: "1px solid #dbe8f5",
              boxShadow:
                "0px 3px 3px -2px rgba(38,54,70,.16), 0px 3px 4px 0px rgba(38,54,70,.10), 0px 1px 8px 0px rgba(38,54,70,.08)",
              padding: "4px 0",
              pointerEvents: "auto"
            }
          }
        }}
      >
        <MenuList disablePadding autoFocusItem={false} onMouseEnter={cancelCloseShare} onMouseLeave={closeShare}>
          <ShareMenuItem
            icon={<LockOutlinedIcon sx={iconSx} />}
            label="Only me"
            selected={bookmark.visibility === "private"}
            onClick={() => handleShareScope("private")}
          />
          <ShareMenuItem
            icon={<PeopleAltOutlinedIcon sx={iconSx} />}
            label={
              bookmark.visibility === "shared" && bookmark.shared_with?.length
                ? `Specific people (${bookmark.shared_with.length})…`
                : "Specific people…"
            }
            selected={bookmark.visibility === "shared"}
            onClick={handleOpenPeoplePicker}
          />
          <ShareMenuItem
            icon={<PublicOutlinedIcon sx={iconSx} />}
            label="Everyone on my team"
            selected={bookmark.visibility === "account"}
            onClick={() => handleShareScope("account")}
          />
        </MenuList>
      </Popover>
    </>
  );
};

const menuItemSx = {
  fontSize: "14px",
  color: "#071f35",
  padding: "6px 12px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  minHeight: "auto"
};

const iconSx = {
  fontSize: "16px",
  color: "#5e7a95"
};

const labelSx = {
  fontSize: "14px",
  flex: 1
};

const SHARE_SUMMARY_LABEL: Record<IBookmark["visibility"], string> = {
  private: "Only me",
  shared: "Specific people",
  account: "My team"
};

const ShareSummary: React.FC<{ visibility: IBookmark["visibility"] }> = ({ visibility }) => (
  <Typography sx={{ fontSize: "11px", color: "#5e7a95", ml: "auto" }}>{SHARE_SUMMARY_LABEL[visibility]}</Typography>
);

interface ShareMenuItemProps {
  icon: React.ReactNode;
  label: string;
  selected: boolean;
  disabled?: boolean;
  onClick: (e?: React.MouseEvent) => void;
}

const ShareMenuItem: React.FC<ShareMenuItemProps> = ({ icon, label, selected, disabled, onClick }) => (
  <MenuItem
    onClick={onClick}
    disabled={disabled}
    sx={{
      ...menuItemSx,
      backgroundColor: selected ? "#dbe8f5" : "transparent",
      "&:hover": { backgroundColor: selected ? "#dbe8f5" : "#ecf1f8" }
    }}
  >
    {icon}
    <Typography sx={labelSx}>{label}</Typography>
    {selected && <CheckIcon sx={{ fontSize: "14px", color: "#071f35" }} />}
  </MenuItem>
);
